import { useState } from 'react';
import type { ComponentTypeDto, TariffGroupDto } from '../../lib/api-types';
import { toDisplayError } from '../../lib/errors';
import { Alert } from '../../components/ui/Alert';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Dialog } from '../../components/ui/Dialog';
import { Label } from '../../components/ui/Label';
import { Select } from '../../components/ui/Select';
import { Spinner } from '../../components/ui/Spinner';
import {
  useAddComponentTypeUnit,
  useAddTariffGroupMembers,
  useComponentTypes,
  useCreateComponentType,
  useCreateTariffGroup,
  useCreateTechnicalCategory,
  useReferentialMeasurementUnits,
  useRemoveComponentTypeUnit,
  useRemoveTariffGroupMember,
  useTariffGroups,
  useTechnicalCategories,
} from './hooks';
import { CodeNameForm } from './shared';

/**
 * « Types & unités » : catégories techniques, types de composants (avec leurs
 * unités autorisées), unités de mesure (lecture seule) et groupes tarifaires.
 */
export function TypesUnitesTab(): JSX.Element {
  return (
    <div className="space-y-4">
      <CategoriesCard />
      <ComponentTypesCard />
      <MeasurementUnitsCard />
      <TariffGroupsCard />
    </div>
  );
}

function CategoriesCard(): JSX.Element {
  const query = useTechnicalCategories();
  const create = useCreateTechnicalCategory();
  const [creating, setCreating] = useState(false);

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle>Catégories techniques</CardTitle>
          <Button
            size="sm"
            onClick={() => {
              create.reset();
              setCreating(true);
            }}
          >
            Nouvelle catégorie
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {query.isLoading ? (
          <Spinner label="Chargement des catégories…" />
        ) : query.isError ? (
          <Alert variant="error">{toDisplayError(query.error).message}</Alert>
        ) : query.data && query.data.length > 0 ? (
          <ul className="divide-y divide-slate-100">
            {query.data.map((c) => (
              <li key={c.id} className="flex items-center gap-2 py-2">
                <span className="text-sm font-medium text-slate-800">{c.name}</span>
                <span className="font-mono text-xs text-slate-500">{c.code}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-slate-500">Aucune catégorie technique.</p>
        )}
      </CardContent>

      <Dialog open={creating} onClose={() => setCreating(false)} title="Nouvelle catégorie">
        <div className="space-y-4">
          {create.isError ? (
            <Alert variant="error" details={toDisplayError(create.error).details}>
              {toDisplayError(create.error).message}
            </Alert>
          ) : null}
          <CodeNameForm
            idPrefix="category-create"
            pending={create.isPending}
            onCancel={() => setCreating(false)}
            onSubmit={(values) => create.mutate(values, { onSuccess: () => setCreating(false) })}
          />
        </div>
      </Dialog>
    </Card>
  );
}

function ComponentTypesCard(): JSX.Element {
  const categoriesQuery = useTechnicalCategories();
  const typesQuery = useComponentTypes();
  const unitsQuery = useReferentialMeasurementUnits();
  const create = useCreateComponentType();
  const addUnit = useAddComponentTypeUnit();
  const removeUnit = useRemoveComponentTypeUnit();

  const [creating, setCreating] = useState(false);
  const [categoryId, setCategoryId] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);

  const categories = categoriesQuery.data ?? [];
  const units = unitsQuery.data ?? [];

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle>Types de composants</CardTitle>
          <Button
            size="sm"
            onClick={() => {
              create.reset();
              setCreateError(null);
              setCategoryId('');
              setCreating(true);
            }}
          >
            Nouveau type de composant
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {addUnit.isError ? (
          <Alert variant="error">{toDisplayError(addUnit.error).message}</Alert>
        ) : null}
        {removeUnit.isError ? (
          <Alert variant="error">{toDisplayError(removeUnit.error).message}</Alert>
        ) : null}

        {typesQuery.isLoading ? (
          <Spinner label="Chargement des types de composants…" />
        ) : typesQuery.isError ? (
          <Alert variant="error">{toDisplayError(typesQuery.error).message}</Alert>
        ) : typesQuery.data && typesQuery.data.length > 0 ? (
          <ul className="divide-y divide-slate-100">
            {typesQuery.data.map((t) => (
              <ComponentTypeRow
                key={t.id}
                componentType={t}
                availableUnits={units}
                onAddUnit={(measurementUnitId) =>
                  addUnit.mutate({ componentTypeId: t.id, measurementUnitId })
                }
                onRemoveUnit={(unitId) => removeUnit.mutate({ componentTypeId: t.id, unitId })}
              />
            ))}
          </ul>
        ) : (
          <p className="text-sm text-slate-500">Aucun type de composant.</p>
        )}
      </CardContent>

      <Dialog open={creating} onClose={() => setCreating(false)} title="Nouveau type de composant">
        <div className="space-y-4">
          {create.isError ? (
            <Alert variant="error" details={toDisplayError(create.error).details}>
              {toDisplayError(create.error).message}
            </Alert>
          ) : null}
          <CodeNameForm
            idPrefix="component-type-create"
            pending={create.isPending}
            onCancel={() => setCreating(false)}
            onSubmit={(values) => {
              if (!categoryId) {
                setCreateError('Veuillez choisir une catégorie technique.');
                return;
              }
              setCreateError(null);
              create.mutate({ categoryId, ...values }, { onSuccess: () => setCreating(false) });
            }}
          >
            <div>
              <Label htmlFor="component-type-category">Catégorie technique</Label>
              <Select
                id="component-type-category"
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
              >
                <option value="">— Choisir une catégorie —</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({c.code})
                  </option>
                ))}
              </Select>
              {createError ? <p className="mt-1 text-sm text-red-600">{createError}</p> : null}
            </div>
          </CodeNameForm>
        </div>
      </Dialog>
    </Card>
  );
}

function ComponentTypeRow({
  componentType,
  availableUnits,
  onAddUnit,
  onRemoveUnit,
}: {
  componentType: ComponentTypeDto;
  availableUnits: { id: string; code: string; name: string }[];
  onAddUnit: (measurementUnitId: string) => void;
  onRemoveUnit: (unitId: string) => void;
}): JSX.Element {
  const assigned = componentType.units ?? [];
  const assignedIds = new Set(assigned.map((u) => u.id));
  const addable = availableUnits.filter((u) => !assignedIds.has(u.id));

  return (
    <li className="flex flex-wrap items-center gap-2 py-2">
      <span className="text-sm font-medium text-slate-800">{componentType.name}</span>
      <span className="font-mono text-xs text-slate-500">{componentType.code}</span>
      <span className="flex flex-wrap gap-1">
        {assigned.map((u) => (
          <span
            key={u.id}
            className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700"
          >
            {u.code}
            <button
              type="button"
              aria-label={`Retirer l’unité ${u.code} de ${componentType.code}`}
              className="text-slate-400 hover:text-red-600"
              onClick={() => onRemoveUnit(u.id)}
            >
              ×
            </button>
          </span>
        ))}
        {assigned.length === 0 ? (
          <span className="text-xs text-slate-400">Aucune unité autorisée</span>
        ) : null}
      </span>
      {addable.length > 0 ? (
        <span className="ml-auto">
          <Select
            aria-label={`Ajouter une unité à ${componentType.code}`}
            className="h-8 w-auto text-xs"
            value=""
            onChange={(e) => {
              if (e.target.value) onAddUnit(e.target.value);
            }}
          >
            <option value="">+ Unité…</option>
            {addable.map((u) => (
              <option key={u.id} value={u.id}>
                {u.code} — {u.name}
              </option>
            ))}
          </Select>
        </span>
      ) : null}
    </li>
  );
}

function MeasurementUnitsCard(): JSX.Element {
  const query = useReferentialMeasurementUnits();

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle>Unités de mesure</CardTitle>
          <Badge tone="neutral">Lecture seule</Badge>
        </div>
      </CardHeader>
      <CardContent>
        {query.isLoading ? (
          <Spinner label="Chargement des unités…" />
        ) : query.isError ? (
          <Alert variant="error">{toDisplayError(query.error).message}</Alert>
        ) : query.data && query.data.length > 0 ? (
          <ul className="flex flex-wrap gap-2">
            {query.data.map((u) => (
              <li
                key={u.id}
                className="rounded-md border border-slate-200 px-3 py-1.5 text-sm text-slate-700"
              >
                <span className="font-mono font-medium">{u.code}</span> — {u.name}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-slate-500">Aucune unité de mesure.</p>
        )}
      </CardContent>
    </Card>
  );
}

function TariffGroupsCard(): JSX.Element {
  const groupsQuery = useTariffGroups();
  const typesQuery = useComponentTypes();
  const create = useCreateTariffGroup();
  const addMembers = useAddTariffGroupMembers();
  const removeMember = useRemoveTariffGroupMember();

  const [creating, setCreating] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [pickedTypeIds, setPickedTypeIds] = useState<string[]>([]);

  const groups = groupsQuery.data ?? [];
  const selectedGroup = groups.find((g) => g.id === selectedGroupId) ?? null;
  const componentTypes = typesQuery.data ?? [];

  const togglePicked = (id: string) =>
    setPickedTypeIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle>Groupes tarifaires</CardTitle>
          <Button
            size="sm"
            onClick={() => {
              create.reset();
              setCreating(true);
            }}
          >
            Nouveau groupe tarifaire
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {groupsQuery.isLoading ? (
          <Spinner label="Chargement des groupes tarifaires…" />
        ) : groupsQuery.isError ? (
          <Alert variant="error">{toDisplayError(groupsQuery.error).message}</Alert>
        ) : groups.length > 0 ? (
          <ul className="flex flex-wrap gap-2">
            {groups.map((g) => (
              <li key={g.id}>
                <Button
                  size="sm"
                  variant={selectedGroupId === g.id ? 'primary' : 'outline'}
                  aria-pressed={selectedGroupId === g.id}
                  onClick={() => {
                    setSelectedGroupId(g.id);
                    setPickedTypeIds([]);
                  }}
                >
                  {g.name} ({g.code})
                </Button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-slate-500">Aucun groupe tarifaire.</p>
        )}

        {selectedGroup ? (
          <GroupMembersEditor
            group={selectedGroup}
            componentTypes={componentTypes}
            pickedTypeIds={pickedTypeIds}
            onTogglePicked={togglePicked}
            addPending={addMembers.isPending}
            addError={addMembers.error}
            removeError={removeMember.error}
            onAdd={() => {
              if (pickedTypeIds.length === 0) return;
              addMembers.mutate(
                { groupId: selectedGroup.id, componentTypeIds: pickedTypeIds },
                { onSuccess: () => setPickedTypeIds([]) },
              );
            }}
            onRemove={(componentTypeId) =>
              removeMember.mutate({ groupId: selectedGroup.id, componentTypeId })
            }
          />
        ) : null}
      </CardContent>

      <Dialog open={creating} onClose={() => setCreating(false)} title="Nouveau groupe tarifaire">
        <div className="space-y-4">
          {create.isError ? (
            <Alert variant="error" details={toDisplayError(create.error).details}>
              {toDisplayError(create.error).message}
            </Alert>
          ) : null}
          <CodeNameForm
            idPrefix="tariff-group-create"
            pending={create.isPending}
            onCancel={() => setCreating(false)}
            onSubmit={(values) => create.mutate(values, { onSuccess: () => setCreating(false) })}
          />
        </div>
      </Dialog>
    </Card>
  );
}

function GroupMembersEditor({
  group,
  componentTypes,
  pickedTypeIds,
  onTogglePicked,
  addPending,
  addError,
  removeError,
  onAdd,
  onRemove,
}: {
  group: TariffGroupDto;
  componentTypes: ComponentTypeDto[];
  pickedTypeIds: string[];
  onTogglePicked: (id: string) => void;
  addPending: boolean;
  addError: unknown;
  removeError: unknown;
  onAdd: () => void;
  onRemove: (componentTypeId: string) => void;
}): JSX.Element {
  const members = group.members ?? [];
  const memberIds = new Set(members.map((m) => m.componentTypeId));
  const addable = componentTypes.filter((t) => !memberIds.has(t.id));
  const typeById = new Map(componentTypes.map((t) => [t.id, t]));

  return (
    <div className="space-y-3 rounded-md border border-slate-200 p-3">
      <p className="text-sm font-medium text-slate-800">Membres du groupe « {group.name} »</p>
      {addError ? <Alert variant="error">{toDisplayError(addError).message}</Alert> : null}
      {removeError ? <Alert variant="error">{toDisplayError(removeError).message}</Alert> : null}

      {members.length > 0 ? (
        <ul className="flex flex-wrap gap-1">
          {members.map((m) => {
            const label = m.name ?? typeById.get(m.componentTypeId)?.name ?? m.componentTypeId;
            return (
              <li
                key={m.componentTypeId}
                className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700"
              >
                {label}
                <button
                  type="button"
                  aria-label={`Retirer ${label} du groupe ${group.code}`}
                  className="text-slate-400 hover:text-red-600"
                  onClick={() => onRemove(m.componentTypeId)}
                >
                  ×
                </button>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="text-sm text-slate-500">Aucun membre pour le moment.</p>
      )}

      {addable.length > 0 ? (
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-wide text-slate-400">Ajouter des types</p>
          <ul className="grid gap-1 sm:grid-cols-2">
            {addable.map((t) => (
              <li key={t.id}>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={pickedTypeIds.includes(t.id)}
                    onChange={() => onTogglePicked(t.id)}
                  />
                  {t.name} ({t.code})
                </label>
              </li>
            ))}
          </ul>
          <Button
            size="sm"
            onClick={onAdd}
            loading={addPending}
            disabled={pickedTypeIds.length === 0}
          >
            Ajouter au groupe
          </Button>
        </div>
      ) : null}
    </div>
  );
}
